import TableC from "@/CommonElements/TableC";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { Info } from "@/Component/Info";
import { LabelValue } from "@/Component/Value";
import { useGetMadinaAllBagsQuery, useGetMadinaOneBagQuery, useMadinaDeleteBagMutation } from "@/services/arrivals/madinah";
import { DATE_FORMAT, DATE_TIME_FORMAT, MODES, permissions } from "@/utils/constants";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import usePermissions from "@/utils/hooks/usePermissions";
import { useSystem } from "@/utils/hooks/useLocation";
import { DeleteIcon, Edit02Icon, PrinterIcon } from "@hugeicons/core-free-icons/index";
import { HugeiconsIcon } from "@hugeicons/react";
import { Divider, Flex } from "antd";
import { useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router";
import { BagsForm, usePrintBag, usePrintBagList } from "../MadinahReceptionList";
import { FormProvider, useForm } from "react-hook-form";
import { handleFormErrors, PickKeyPairs } from "@/utils/helpers";
import { Spinner } from "reactstrap";
import { useUpdateBagMutation } from "@/services/madinah/bag";
import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import { useConfirm } from "@/utils/hooks/useConfrim";
import BtnAsLink from "@/Component/BtnAsLink";
import dayjs from "dayjs";

export const BagsList = () => {
  const { confirm, ModalComponent } = useConfirm();
  const [currentPage, setCurrentPage] = useState(1);
  const {
    searchValue,
    debouncedValue,
    setSearchValue } = useDebouncedSearch("", 750);
  const { getLocalizedDate, getLocalizedText, getLocalizedNumber } = useLocalizedText();
  const {
    filters,
    handleFilterApply, } = useFilter({});

  const { t } = useTranslation();
  const { from_id } = useSystem();
  const {
    hasAnyPermission
  } = usePermissions();
  const canManage = hasAnyPermission([permissions.manage_arrival_bags_for_madina]);
  const canUpdate = hasAnyPermission([permissions.update_arrival_bags_for_madina]);
  const canDelete = hasAnyPermission([permissions.delete_arrival_bags_for_madina]);

  // ============
  // Side Effect
  // ============
  const { printBagsMutation, isPrintBagPending } = usePrintBagList();
  const { printBagAction } = usePrintBag();
  const params = {
    page: currentPage,
    per_page: 10,
    ...filters,
    created_at: filters.created_at ? dayjs(filters.created_at).format(DATE_FORMAT) : undefined,
    search: debouncedValue,
  };
  const { data: bagsRes, isLoading } = useGetMadinaAllBagsQuery(params);

  const bagsData = bagsRes?.data?.data || [];
  const meta = bagsRes?.data?.meta || {};

  const columns = [
    {
      name: t("BAG_NUMBER"),
      key: "bag_number",
    },
    {
      name: t("ARRIVAL_CARD_REQUEST_NUMBER"),
      key: "request_number",
    },
    {
      name: t("PILGRIMS_COUNT_IN_BAG"), // 
      key: "pilgrims_count",
    },
    {
      name: t("BAG_TYPE"),
      key: "bag_type",
      render: (value) => {
        const mapDisplay = {
          "manifest": t("FROM_PORTS"),
          "tafweej": t("BETWEEN_CITIES"),
        }
        return mapDisplay[value] || "-";
      },
    },
    {
      name: t("عدد الجوازات"),
      key: "actual_passports_count",
      render: (value, rowData) => <LabelValue.Value value={value || "-"} />,
      visible: false

    },
    {
      name: t("FLIGHT_NUMBER"), // 
      key: "flight_number", //
    },
    {
      name: t("HOUSING_CONTRACT"),
      key: "house_contract_id",
      render: (value, rowData) => <Flex gap={8} align="center">
        <Info title={rowData?.house_name_ar} />
        <LabelValue.Value value={value || "-"} />
      </Flex>,
    },
    {
      name: t("EXPECTED_PILGRIMS_COUNT"),
      key: "expected_pilgrims_count",

      render: (value, rowData) => <Flex gap={8} align="center">
        <Info title={
          <ul>
            <li>{t("PASSPORT_IMAGES_COUNT")}: {getLocalizedNumber(rowData?.passport_images_count) || "-"}</li>
            <li>{t("PILGRIMS_WITHOUT_PASSPORT_COUNT")}: {getLocalizedNumber(rowData?.pilgrims_without_passport_count) || "-"}</li>
            <li>{t("ACTUAL_PASSPORTS_COUNT")}: {getLocalizedNumber(rowData?.actual_passports_count) || "-"}</li>
          </ul>
        } />
        <LabelValue.Value value={value || "-"} />
      </Flex>,
    },
    {
      name: t("AIRPORT_DEPARTURE_TIME"),
      key: "flight_date",
      render: (value) => (!value && "-") || getLocalizedDate(value, DATE_TIME_FORMAT),
    },
    {
      name: t("DELEGATION"),
      key: "mission_name_ar",
      render: ($, rowData) => getLocalizedText(rowData?.mission_name_ar, rowData?.mission_name_en),
    },
    {
      name: t("CREATED_AT"),
      key: "created_at",
      render: (value) => <span className="text-nowrap">{(!value && "-") || getLocalizedDate(value, DATE_TIME_FORMAT)}</span>,
    },
    {
      name: t("ACTIONS"),
      key: "actions",
    },
  ];

  const filterFields = [
    {
      label: t("BAG_NUMBER"),
      name: "bag_number",
      type: "input",
    },
    {
      label: t("ARRIVAL_CARD_REQUEST_NUMBER"),
      name: "request_number",
      type: "input",
    },
    {
      label: t("FLIGHT_NUMBER"), // 
      name: "flight_number", //
      type: "input",
    },
    {
      label: t("عقد السكن"),
      name: "house_contract_id",
      type: "select",
      optionKey: "madinahContracts",
      options: (c) => ({
        value: c.id,
        label: (
          <LabelValue.Value
            value={`${c.id} - ${c.house_commercial_name_ar}`}
          />
        ),
      })
    },
    {
      label: t("موعد الاقلاع"),
      name: "flight_date",
      type: "date",
    },
    {
      label: t("موعد الوصول"),
      name: "arrival_at",
      type: "date",
    },
    {
      label: t("ORGANIZER"),
      name: "mission_id",
      type: "select",
      optionKey: "missions",
      options: (m) =>
      ({
        value: m.id,
        label: (
          <LabelValue.Value
            value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")}
          />
        ),
      } || []),
    },
    {
      label: t("BAG_TYPE"),
      name: "bag_type",
      type: "select",
      options: [
        { value: "manifest", label: t("FROM_PORTS") },
        { value: "tafweej", label: t("BETWEEN_CITIES") },
      ],
    },
    {
      label: t("FLIGHT_DATE"),
      name: t("FLIGHT_DATE"),
      key: "flight_date",
      type: "date",
    },
    {
      label: t("CREATED_AT"),
      name: "created_at",
      type: "date",
    },
  ];

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const onFilterApply = handleFilterApply(() => setCurrentPage(1));
  const loading = isLoading
  const { mutate: deleteBagMutation, isPending: isDeleteBagPending } = useMadinaDeleteBagMutation();

  // Define actions function
  const getActions = (rowData) => {
    const items = [];
    if (canUpdate) {
      items.push({
        key: "edit",
        label: (
          <Link
            to={`${rowData.id}/request/${rowData.request_number}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      });
    }
    if (canManage) {
      items.push(
        printBagAction({
          request_number: rowData.request_number,
          id: rowData.id,
          bag_number: rowData.bag_number,
        })
      );
    }
    if (canDelete) {
      if (items.length) items.push({ type: "divider" });
      items.push({
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => {
          confirm({
            title: t("CONFIRM"),
            content: t("CONFIRM_DELETE_BAG_MESSAGE"),
            okText: t("CONFIRM"),
            cancelText: t("CANCEL"),
            onOk: ({ closeModal }) => {
              deleteBagMutation(
                { request_number: rowData.request_number, bag_id: rowData.id },
                {
                  onSuccess: () => {
                    closeModal();
                  },
                }
              );

              return false;
            },
            onCancel: () => { },
          });
        },
        danger: true,
      });
    }
    return items;
  };

  const hasValidReportMenditoryFilters = !!Object.keys(filters)?.length;

  return (
    <>
      <TableC
        searchValue={searchValue}
        onSearchChange={(e) => setSearchValue(e.target.value)}
        data={bagsData}
        columns={columns}
        loading={loading}
        pagination={meta ? {
          current: currentPage,
          total: meta?.total || 0,
          pageSize: meta?.per_page || 10,
          onChange: handlePageChange,
        } : undefined}
        filters={filters}
        onFilterApply={onFilterApply}
        // onFilterReset={handleFilterReset()}
        filterFields={filterFields}
        actions={getActions}
        renderActions={
          canManage ? (
            <div className="position-relative" style={{ zIndex: 0 }}>
              {!hasValidReportMenditoryFilters && (
                <div
                  style={{ zIndex: 1, bottom: "100%", right: "2px", transform: "translateY(calc(100% + 2px))" }}
                  className="position-absolute"
                >
                  <Info title={!hasValidReportMenditoryFilters ? t("NO_FILTERS_PROVIDED") : ""} />
                </div>
              )}
              <BtnAsLink
                disabled={!hasValidReportMenditoryFilters || isPrintBagPending}
                icon={<HugeiconsIcon icon={PrinterIcon} />}
                onClick={() => printBagsMutation(params)}
              >
                {t("PRINT")}
              </BtnAsLink>
            </div>
          ) : null
        }

      />
      <ModalComponent loading={isDeleteBagPending} />
      <BagModal />
    </>
  );
};

const BagModal = () => {
  const { bag_id, request_number, mode } = useParams();
  const isViewMode = mode === "view";
  const form = useForm();
  const { t } = useTranslation();
  const { from_id } = useSystem();
  const {
    hasAnyPermission
  } = usePermissions();
  const canUpdate = hasAnyPermission([permissions.update_arrival_bags_for_madina]);
  const modes = Object.values(MODES).filter(m => m !== MODES.create);
  const navigate = useNavigate();

  // ==================
  // Side Effects
  // ==================
  const { mutate: updateBag, isPending: isUpdateBagPending } = useUpdateBagMutation();
  const queryClient = useQueryClient();
  const { data: bagRes, isLoading: isBagLoading } = useGetMadinaOneBagQuery();

  const bagData = useMemo(() => bagRes?.data?.data, [bagRes]);

  useEffect(() => {
    const pickedFields = PickKeyPairs(bagData || {}, ["bag_number", "expected_pilgrims_count", "actual_passports_count", "classification_type", "notes", "pilgrims_without_passport_count", "passport_images_count"])
    if (pickedFields) {
      form.reset(pickedFields);
    }
  }, [bagData]);

  const showModal = !!bag_id && modes.includes(mode);

  const onSubmit = (data) => {
    updateBag({
      request_number: request_number,
      id: bag_id,
      ...data,
    }, {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(["madina-one-bag", variables.request_number, variables.id], (old) => {
          return produce(old, (draft) => {
            draft.data.data = data.data.data;
          })
        });
        navigate(`/app/madina/bags/${bag_id}/request/${request_number}/view`);
      },
      onError: (err) => {
        handleFormErrors(err, form.setError, t, "FAILD_TO_SAVE")
      }
    });
  };

  const backTo = `/app/madina/bags`;

  return (
    <Modal show={showModal} centered size="lg">
      <Modal.Body>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>

            <h2>{t("EDIT_BAG")}</h2>
            <Divider />

            <BagsForm isViewMode={isViewMode} isLoading={isBagLoading} />

            <ActionsFooter>
              {isViewMode && canUpdate && (
                <Link
                  to={`/app/madina/bags/${bag_id}/request/${request_number}/edit`}
                  className="btn btn-primary btn-outline btn-sm"
                >
                  {t("EDIT")}
                </Link>
              )}
              {!isViewMode && <Button className="btn btn-primary btn-sm" type="submit" disabled={isUpdateBagPending}>{isUpdateBagPending ? <Spinner size="sm" /> : t("SAVE")}</Button>}
              <Link style={{ pointerEvents: isUpdateBagPending ? 'none' : 'auto' }} to={backTo} className="btn btn-danger btn-sm" disabled={isUpdateBagPending}>{t("CANCEL")}</Link>
            </ActionsFooter>
          </form>
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
};